import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  IoChatbubbleEllipsesOutline,
  IoCloseOutline,
  IoMailUnreadOutline,
  IoPersonAddOutline,
  IoSendOutline,
} from "react-icons/io5";
import { useAuth } from "../services/AuthContext";
import {
  buildDirectMessageUnreadSummary,
  DIRECT_MESSAGE_OPEN_EVENT,
  DIRECT_MESSAGE_PANEL_OPEN_EVENT,
  DIRECT_MESSAGE_UNREAD_EVENT,
  DIRECT_MESSAGE_UNREAD_GLOBAL_KEY,
  getConversationAttentionCount,
} from "../services/directMessageUi";
import { normalizeLanguage } from "../services/language";

const MAX_MESSAGE_LENGTH = 280;

const COPY = {
  es: {
    messages: "Mensajes",
    newMessage: "Nuevo mensaje",
    writeTo: "Escribir a",
    placeholder: "Escribe tu mensaje...",
    send: "Enviar",
    close: "Cerrar",
    requestFrom: "quiere enviarte un mensaje.",
    privacy: "El contenido permanecerá oculto hasta que aceptes.",
    acceptRead: "Aceptar y leer",
    reject: "Rechazar",
    pending: "Esperando que el jugador acepte tu mensaje.",
    rejected: "La solicitud fue rechazada.",
    registerTitle: "Regístrate para responder",
    registerBody: "Conservaremos este mensaje y la conversación al convertir tu cuenta invitada.",
    registerSendBody: "Para responder debes registrarte primero.",
    register: "Registrarme",
    newFrom: "Nuevo mensaje de",
    openMessage: "Abrir mensaje",
    noMessages: "Todavía no tienes conversaciones.",
    delivered: "Entregado",
    read: "Leído",
    genericError: "No se pudo completar la operación.",
  },
  en: {
    messages: "Messages",
    newMessage: "New message",
    writeTo: "Write to",
    placeholder: "Write your message...",
    send: "Send",
    close: "Close",
    requestFrom: "wants to send you a message.",
    privacy: "The content stays hidden until you accept.",
    acceptRead: "Accept and read",
    reject: "Reject",
    pending: "Waiting for the player to accept your message.",
    rejected: "The request was rejected.",
    registerTitle: "Register to reply",
    registerBody: "We will keep this message and conversation when your guest account is converted.",
    registerSendBody: "You must register before you can reply.",
    register: "Register",
    newFrom: "New message from",
    openMessage: "Open message",
    noMessages: "You do not have conversations yet.",
    delivered: "Delivered",
    read: "Read",
    genericError: "The operation could not be completed.",
  },
  de: {
    messages: "Nachrichten",
    newMessage: "Neue Nachricht",
    writeTo: "Schreiben an",
    placeholder: "Nachricht schreiben...",
    send: "Senden",
    close: "Schließen",
    requestFrom: "möchte dir eine Nachricht senden.",
    privacy: "Der Inhalt bleibt verborgen, bis du annimmst.",
    acceptRead: "Annehmen und lesen",
    reject: "Ablehnen",
    pending: "Warte darauf, dass der Spieler deine Nachricht annimmt.",
    rejected: "Die Anfrage wurde abgelehnt.",
    registerTitle: "Registriere dich zum Antworten",
    registerBody: "Nachricht und Unterhaltung bleiben bei der Umwandlung deines Gastkontos erhalten.",
    registerSendBody: "Du musst dich zuerst registrieren, um zu antworten.",
    register: "Registrieren",
    newFrom: "Neue Nachricht von",
    openMessage: "Nachricht öffnen",
    noMessages: "Du hast noch keine Unterhaltungen.",
    delivered: "Zugestellt",
    read: "Gelesen",
    genericError: "Der Vorgang konnte nicht abgeschlossen werden.",
  },
  ru: {
    messages: "Сообщения",
    newMessage: "Новое сообщение",
    writeTo: "Написать",
    placeholder: "Введите сообщение...",
    send: "Отправить",
    close: "Закрыть",
    requestFrom: "хочет отправить вам сообщение.",
    privacy: "Текст будет скрыт, пока вы не примете запрос.",
    acceptRead: "Принять и прочитать",
    reject: "Отклонить",
    pending: "Ожидаем принятия сообщения игроком.",
    rejected: "Запрос отклонен.",
    registerTitle: "Зарегистрируйтесь, чтобы ответить",
    registerBody: "Сообщение и беседа сохранятся после преобразования гостевого аккаунта.",
    registerSendBody: "Чтобы ответить, сначала зарегистрируйтесь.",
    register: "Регистрация",
    newFrom: "Новое сообщение от",
    openMessage: "Открыть сообщение",
    noMessages: "У вас пока нет бесед.",
    delivered: "Доставлено",
    read: "Прочитано",
    genericError: "Не удалось выполнить операцию.",
  },
};

const isGuestSessionUser = (userValue) => {
  if (userValue?.isGuest === true) return true;
  return [userValue?.loginIdentifier, userValue?.email, userValue?.username].some((value) => {
    const normalized = String(value || "").trim().toLowerCase();
    return (
      normalized.startsWith("invitadoo") ||
      normalized.endsWith("@guest.xima.local")
    );
  });
};

const normalizeConversation = (value) => {
  if (!value || typeof value !== "object") return null;
  const id = String(value.id || value._id || "").trim();
  if (!id) return null;
  return {
    ...value,
    id,
    status: String(value.status || "pending"),
    viewerRole: String(value.viewerRole || ""),
    otherPlayer: value.otherPlayer && typeof value.otherPlayer === "object"
      ? value.otherPlayer
      : { name: "Jugador", publicId: "" },
    messages: Array.isArray(value.messages) ? value.messages : [],
    unreadCount: Math.max(0, Number(value.unreadCount) || 0),
  };
};

const sortConversations = (values) =>
  [...values].sort(
    (left, right) =>
      (Date.parse(right?.lastMessageAt || right?.updatedAt || 0) || 0) -
      (Date.parse(left?.lastMessageAt || left?.updatedAt || 0) || 0)
  );

const DirectMessages = ({ language = "es" }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const socketRef = useRef(null);
  const registrationPromptRef = useRef(null);
  const [conversations, setConversations] = useState([]);
  const [panelOpen, setPanelOpen] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState("");
  const [composeTarget, setComposeTarget] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [registrationPromptOpen, setRegistrationPromptOpen] = useState(false);
  const [useMobilePlacement, setUseMobilePlacement] = useState(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia("(max-width: 820px) and (orientation: portrait)").matches
      : false
  );
  const copy = COPY[normalizeLanguage(language)] || COPY.es;
  const currentUserIsGuest = isGuestSessionUser(user);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const mediaQuery = window.matchMedia("(max-width: 820px) and (orientation: portrait)");
    const applyPlacement = () => setUseMobilePlacement(mediaQuery.matches);
    applyPlacement();
    mediaQuery.addEventListener?.("change", applyPlacement);
    return () => mediaQuery.removeEventListener?.("change", applyPlacement);
  }, []);

  const upsertConversation = useCallback((rawConversation) => {
    const normalized = normalizeConversation(rawConversation);
    if (!normalized) return;
    setConversations((previous) => {
      const index = previous.findIndex((entry) => entry.id === normalized.id);
      if (index < 0) return sortConversations([normalized, ...previous]);
      const next = [...previous];
      next[index] = normalized;
      return sortConversations(next);
    });
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    let activeSocket = null;
    let retryTimer = null;
    let disposed = false;
    let unbind = () => {};

    const bind = (socket) => {
      activeSocket = socket;
      socketRef.current = socket;
      const historyRetryTimers = [];
      const handleHistory = (payload) => {
        const source = Array.isArray(payload) ? payload : [];
        setConversations(sortConversations(source.map(normalizeConversation).filter(Boolean)));
      };
      const handleIncoming = (payload) => {
        const normalized = normalizeConversation(payload);
        if (!normalized) return;
        upsertConversation(normalized);
      };
      const handleSent = (payload) => {
        const normalized = normalizeConversation(payload);
        if (!normalized) return;
        upsertConversation(normalized);
        setComposeTarget(null);
        setActiveConversationId(normalized.id);
        setBusy(false);
        setDraft("");
      };
      const handleConversation = (payload) => {
        const normalized = normalizeConversation(payload);
        if (!normalized) return;
        upsertConversation(normalized);
        setBusy(false);
      };
      const handleError = (payload) => {
        setBusy(false);
        setErrorText(String(payload?.reason || copy.genericError));
        if (String(payload?.code || "") === "GUEST_REGISTER_REQUIRED") {
          setRegistrationPromptOpen(true);
          setPanelOpen(true);
        }
      };
      const requestHistory = () => {
        if (typeof socket.emit === "function") {
          socket.emit("DIRECT_MESSAGES_HISTORY_REQUEST");
        }
      };

      socket.on("DIRECT_MESSAGES_HISTORY", handleHistory);
      socket.on("DIRECT_MESSAGE_REQUEST", handleIncoming);
      socket.on("DIRECT_MESSAGE_RECEIVED", handleIncoming);
      socket.on("DIRECT_MESSAGE_SENT", handleSent);
      socket.on("DIRECT_MESSAGE_CONVERSATION", handleConversation);
      socket.on("DIRECT_MESSAGE_ERROR", handleError);
      socket.on("connect", requestHistory);
      requestHistory();
      historyRetryTimers.push(window.setTimeout(requestHistory, 400));
      historyRetryTimers.push(window.setTimeout(requestHistory, 1600));

      return () => {
        historyRetryTimers.forEach((timer) => window.clearTimeout(timer));
        if (typeof socket.off !== "function") return;
        socket.off("DIRECT_MESSAGES_HISTORY", handleHistory);
        socket.off("DIRECT_MESSAGE_REQUEST", handleIncoming);
        socket.off("DIRECT_MESSAGE_RECEIVED", handleIncoming);
        socket.off("DIRECT_MESSAGE_SENT", handleSent);
        socket.off("DIRECT_MESSAGE_CONVERSATION", handleConversation);
        socket.off("DIRECT_MESSAGE_ERROR", handleError);
        socket.off("connect", requestHistory);
      };
    };

    const tryAttach = () => {
      if (disposed) return;
      const candidate = window.__ximaSocket || window.socket || null;
      if (!candidate || candidate === activeSocket) return;
      unbind();
      unbind = bind(candidate);
    };

    tryAttach();
    retryTimer = window.setInterval(tryAttach, 1000);
    return () => {
      disposed = true;
      if (retryTimer) window.clearInterval(retryTimer);
      unbind();
      socketRef.current = null;
    };
  }, [copy.genericError, upsertConversation]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleOpenComposer = (event) => {
      const player = event?.detail?.player || event?.detail || null;
      const publicId = String(player?.publicId || player?.playerCode || "").trim();
      if (!publicId) return;
      const existing = conversations.find(
        (entry) =>
          String(entry?.otherPlayer?.publicId || entry?.otherPlayer?.playerCode || "") === publicId &&
          entry.status !== "rejected" &&
          entry.status !== "blocked"
      );
      setErrorText("");
      setDraft("");
      setRegistrationPromptOpen(false);
      setPanelOpen(true);
      if (existing) {
        setComposeTarget(null);
        setActiveConversationId(existing.id);
      } else {
        setActiveConversationId("");
        setComposeTarget({
          ...player,
          publicId,
          name: String(player?.name || player?.displayName || "Jugador"),
        });
      }
    };
    window.addEventListener(DIRECT_MESSAGE_OPEN_EVENT, handleOpenComposer);
    return () => window.removeEventListener(DIRECT_MESSAGE_OPEN_EVENT, handleOpenComposer);
  }, [conversations]);

  const activeConversation = useMemo(
    () => conversations.find((entry) => entry.id === activeConversationId) || null,
    [activeConversationId, conversations]
  );
  const unreadSummary = useMemo(
    () => buildDirectMessageUnreadSummary(conversations),
    [conversations]
  );
  const unreadTotal = unreadSummary.total;
  const unreadNotifications = useMemo(
    () => conversations.filter((entry) => getConversationAttentionCount(entry) > 0).slice(0, 3),
    [conversations]
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const publishedSummary = {
      total: unreadSummary.total,
      byPlayerCode: { ...unreadSummary.byPlayerCode },
    };
    window[DIRECT_MESSAGE_UNREAD_GLOBAL_KEY] = publishedSummary;
    window.dispatchEvent(
      new CustomEvent(DIRECT_MESSAGE_UNREAD_EVENT, { detail: publishedSummary })
    );
  }, [unreadSummary]);

  // Opens the messages panel from elsewhere (the Moderno lobby top bar).
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleOpenPanel = () => {
      setErrorText("");
      setRegistrationPromptOpen(false);
      setComposeTarget(null);
      setActiveConversationId((current) => current || conversations[0]?.id || "");
      setPanelOpen(true);
    };
    window.addEventListener(DIRECT_MESSAGE_PANEL_OPEN_EVENT, handleOpenPanel);
    return () => window.removeEventListener(DIRECT_MESSAGE_PANEL_OPEN_EVENT, handleOpenPanel);
  }, [conversations]);

  useEffect(() => {
    if (!panelOpen || activeConversation?.status !== "accepted") return;
    if (!activeConversation?.unreadCount || !socketRef.current) return;
    socketRef.current.emit("DIRECT_MESSAGE_MARK_READ", {
      conversationId: activeConversation.id,
    });
  }, [activeConversation, panelOpen]);

  useEffect(() => {
    if (!panelOpen || !registrationPromptOpen) return undefined;
    const frame = window.requestAnimationFrame(() => {
      registrationPromptRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [panelOpen, registrationPromptOpen]);

  const sendMessage = () => {
    const text = String(draft || "").trim().slice(0, MAX_MESSAGE_LENGTH);
    const socket = socketRef.current;
    if (!text || busy) return;
    if (currentUserIsGuest) {
      setErrorText("");
      setRegistrationPromptOpen(true);
      return;
    }
    if (!socket) return;
    setBusy(true);
    setErrorText("");
    socket.emit("DIRECT_MESSAGE_SEND", {
      text,
      ...(composeTarget
        ? { recipientPlayerCode: composeTarget.publicId }
        : { conversationId: activeConversation?.id }),
    });
  };

  const updateRequest = (eventName) => {
    const socket = socketRef.current;
    if (!socket || !activeConversation?.id || busy) return;
    setBusy(true);
    setErrorText("");
    socket.emit(eventName, { conversationId: activeConversation.id });
  };

  const goToRegistration = () => {
    setRegistrationPromptOpen(false);
    setPanelOpen(false);
    navigate("/register?upgrade=1&returnTo=%2Fgame%2Flobby");
  };

  const renderComposer = (target, conversationId = "") => (
    <div className="xima-direct-compose">
      <label htmlFor="xima-direct-message-input">
        {copy.writeTo} <strong>{target?.name || target?.displayName || "Jugador"}</strong>
      </label>
      <textarea
        id="xima-direct-message-input"
        value={draft}
        maxLength={MAX_MESSAGE_LENGTH}
        placeholder={copy.placeholder}
        onChange={(event) => setDraft(event.target.value.slice(0, MAX_MESSAGE_LENGTH))}
        onKeyDown={(event) => {
          if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            sendMessage();
          }
        }}
      />
      <div className="xima-direct-compose-footer">
        <span>{draft.length}/{MAX_MESSAGE_LENGTH}</span>
        <button type="button" onClick={sendMessage} disabled={busy || !draft.trim() || (!target && !conversationId)}>
          <IoSendOutline aria-hidden="true" /> {copy.send}
        </button>
      </div>
    </div>
  );

  const renderRegistrationPrompt = () =>
    currentUserIsGuest && registrationPromptOpen ? (
      <div className="xima-direct-register-gate" ref={registrationPromptRef}>
        <IoPersonAddOutline aria-hidden="true" />
        <strong>{copy.registerTitle}</strong>
        <p>{copy.registerSendBody} {copy.registerBody}</p>
        <div className="xima-direct-register-actions">
          <button type="button" onClick={goToRegistration}>{copy.register}</button>
          <button
            type="button"
            className="is-secondary"
            onClick={() => setRegistrationPromptOpen(false)}
          >
            {copy.close}
          </button>
        </div>
      </div>
    ) : null;

  const renderConversation = () => {
    if (composeTarget) {
      return (
        <>
          {renderComposer(composeTarget)}
          {renderRegistrationPrompt()}
        </>
      );
    }
    if (!activeConversation) {
      return <div className="xima-direct-empty">{copy.noMessages}</div>;
    }
    const otherName = activeConversation.otherPlayer?.name || "Jugador";
    if (activeConversation.status === "pending") {
      if (activeConversation.viewerRole === "recipient") {
        return (
          <div className="xima-direct-request">
            <IoMailUnreadOutline aria-hidden="true" />
            <h3>{otherName} {copy.requestFrom}</h3>
            <p>{copy.privacy}</p>
            <div className="xima-direct-request-actions">
              <button type="button" className="accept" disabled={busy} onClick={() => updateRequest("DIRECT_MESSAGE_ACCEPT")}>
                {copy.acceptRead}
              </button>
              <button type="button" className="reject" disabled={busy} onClick={() => updateRequest("DIRECT_MESSAGE_REJECT")}>
                {copy.reject}
              </button>
            </div>
          </div>
        );
      }
      return <div className="xima-direct-status">{copy.pending}</div>;
    }
    if (activeConversation.status === "rejected" || activeConversation.status === "blocked") {
      return <div className="xima-direct-status is-rejected">{copy.rejected}</div>;
    }
    return (
      <>
        <div className="xima-direct-thread" aria-live="polite">
          {activeConversation.messages.map((message) => (
            <div
              key={message.id}
              className={`xima-direct-bubble${message.fromCurrentUser ? " is-mine" : ""}`}
            >
              <span>{message.text}</span>
              {message.fromCurrentUser ? (
                <small>{message.readAt ? copy.read : copy.delivered}</small>
              ) : null}
            </div>
          ))}
        </div>
        {renderComposer(activeConversation.otherPlayer, activeConversation.id)}
        {renderRegistrationPrompt()}
      </>
    );
  };

  if (!panelOpen && conversations.length === 0) return null;

  const openMessagesPanel = (conversationId = "") => {
    const nextConversationId =
      conversationId ||
      activeConversationId ||
      unreadNotifications[0]?.id ||
      conversations[0]?.id ||
      "";
    setErrorText("");
    setRegistrationPromptOpen(false);
    setComposeTarget(null);
    if (nextConversationId) setActiveConversationId(nextConversationId);
    setPanelOpen(true);
  };

  const closeMessagesPanel = () => {
    setPanelOpen(false);
    setRegistrationPromptOpen(false);
  };

  const notificationAnchor =
    typeof document === "undefined"
      ? null
      : document.getElementById(
          useMobilePlacement
            ? "xima-direct-notification-mobile-anchor"
            : "xima-direct-notification-desktop-anchor"
        );
  const messageButtonAnchor =
    typeof document === "undefined"
      ? null
      : document.getElementById(
          useMobilePlacement
            ? "xima-direct-message-mobile-anchor"
            : "xima-direct-message-desktop-anchor"
        );
  const messagePanelAnchor =
    typeof document === "undefined" || !useMobilePlacement
      ? null
      : document.getElementById("xima-direct-panel-mobile-anchor");

  const renderNotificationStack = () =>
    unreadNotifications.length > 0 ? (
      <aside
        className={`xima-direct-notification-stack${panelOpen ? " has-open-panel" : ""}`}
        aria-label={copy.messages}
        aria-live="polite"
      >
        {unreadNotifications.map((conversation) => {
          const otherName = conversation.otherPlayer?.name || "Jugador";
          const incomingMessages = conversation.messages.filter(
            (message) => !message.fromCurrentUser
          );
          const latestIncoming = incomingMessages[incomingMessages.length - 1] || null;
          const notificationText =
            conversation.status === "pending"
              ? `${otherName} ${copy.requestFrom}`
              : latestIncoming?.text || `${copy.newFrom} ${otherName}`;
          const attentionCount = getConversationAttentionCount(conversation);
          return (
            <button
              type="button"
              key={conversation.id}
              className="xima-direct-notification"
              onClick={() => openMessagesPanel(conversation.id)}
            >
              <span className="xima-direct-notification-heading">
                <IoMailUnreadOutline aria-hidden="true" />
                <strong>{copy.newFrom} {otherName}</strong>
              </span>
              <span className="xima-direct-notification-preview">{notificationText}</span>
              <span className="xima-direct-notification-open">{copy.openMessage}</span>
              <b>{Math.min(99, attentionCount)}</b>
            </button>
          );
        })}
      </aside>
    ) : null;

  const renderMessageButton = () =>
    !panelOpen ? (
      <button
        type="button"
        className="xima-direct-floating-button"
        aria-label={copy.messages}
        onClick={() => openMessagesPanel()}
      >
        <IoChatbubbleEllipsesOutline aria-hidden="true" />
        {unreadTotal > 0 ? <span>{Math.min(99, unreadTotal)}</span> : null}
      </button>
    ) : null;

  const renderMessagesPanel = () =>
    panelOpen ? (
      <div
        className={`xima-direct-dock${unreadNotifications.length > 0 ? " has-notifications" : ""}`}
      >
        <section
          className="xima-direct-panel"
          role="dialog"
          aria-modal="false"
          aria-label={copy.messages}
        >
          <header>
            <div>
              <IoChatbubbleEllipsesOutline aria-hidden="true" />
              <strong>{composeTarget ? copy.newMessage : copy.messages}</strong>
            </div>
            <button type="button" aria-label={copy.close} onClick={closeMessagesPanel}>
              <IoCloseOutline aria-hidden="true" />
            </button>
          </header>
          {conversations.length > 0 && !composeTarget ? (
            <nav className="xima-direct-conversation-list" aria-label={copy.messages}>
              {conversations.map((conversation) => (
                <button
                  type="button"
                  key={conversation.id}
                  className={conversation.id === activeConversationId ? "is-active" : ""}
                  onClick={() => {
                    setErrorText("");
                    setDraft("");
                    setRegistrationPromptOpen(false);
                    setActiveConversationId(conversation.id);
                  }}
                >
                  <span>{conversation.otherPlayer?.name || "Jugador"}</span>
                  {getConversationAttentionCount(conversation) > 0 ? (
                    <strong>{Math.min(99, getConversationAttentionCount(conversation))}</strong>
                  ) : null}
                </button>
              ))}
            </nav>
          ) : null}
          <div className="xima-direct-panel-body">
            {errorText ? <div className="xima-direct-error" role="alert">{errorText}</div> : null}
            {renderConversation()}
          </div>
        </section>
      </div>
    ) : null;

  return (
    <>
      {notificationAnchor
        ? createPortal(renderNotificationStack(), notificationAnchor)
        : renderNotificationStack()}
      {messageButtonAnchor
        ? createPortal(renderMessageButton(), messageButtonAnchor)
        : renderMessageButton()}
      {messagePanelAnchor
        ? createPortal(renderMessagesPanel(), messagePanelAnchor)
        : renderMessagesPanel()}
    </>
  );
};

export default DirectMessages;
